import type {
  PickPageCollectionsQueryResponse,
  PickPageCollectionsQueryVariables,
} from '~/api/shopify/queries/pickPageCollections.generated';
import type {
  PickPageArticlesQueryResponse,
  PickPageArticlesQueryVariables,
} from '~/api/shopify/queries/pickPageArticles.generated';
import type {
  PickPageProductsQueryResponse,
  PickPageProductsQueryVariables,
} from '~/api/shopify/queries/pickPageProducts.generated';
import type {
  PickPagePagesQueryResponse,
  PickPagePagesQueryVariables,
} from '~/api/shopify/queries/pickPagePages.generated';
import type { IVariantUrlRedirectBaseIDGroups } from '../utils';
import type { IPickPageItem } from '~/modules/modal-pick-page';

import {
  toPickPageItemFromCollection,
  toPickPageItemFromProduct,
  toPickPageItemFromArticle,
  toPickPageItemFromPage,
} from '~/modules/modal-pick-page';
import { PickPageCollectionsDocument } from '~/api/shopify/queries/pickPageCollections.generated';
import { PickPageArticlesDocument } from '~/api/shopify/queries/pickPageArticles.generated';
import { PickPageProductsDocument } from '~/api/shopify/queries/pickPageProducts.generated';
import { PickPagePagesDocument } from '~/api/shopify/queries/pickPagePages.generated';
import { PICK_PAGE_RESOURCE_QUERY_KEY } from '~/constants/storefrontResource';
import { PLATFORM_PRODUCTS_MAX_PAGE_SIZE } from '~/constants/platformProduct';
import { VARIANT_URL_LIVE_TITLE_STALE_TIME_MS } from '../constants';
import { captureSentryError } from '~/exceptions/sentry';
import { shopifyAdminFetcher } from '~/api/utils';
import { useQuery } from '@tanstack/react-query';
import { toPlatformIdsQuery } from '../utils';
import { useCallback, useMemo } from 'react';

const toFirstCount = (count: number): number => Math.min(count, PLATFORM_PRODUCTS_MAX_PAGE_SIZE);

const handleLivePageQueryError = (error: unknown): void => {
  captureSentryError(error, {
    source: 'useVariantUrlRedirectLivePages',
    level: 'warning',
    tags: { experiment: 'Experiment' },
  });
};

export interface IUseVariantUrlRedirectLivePagesParams extends IVariantUrlRedirectBaseIDGroups {
  /**
   * The theme's own templates, to resolve the template each page renders. Undefined resolves no
   * template at all, which is the answer for a caller that holds none — and the one a caller
   * matching template experiments has to read while the theme is still loading, since a page cannot
   * be told to render the default template before then.
   */
  templateAssetKeys?: Set<string>;
}

export interface IUseVariantUrlRedirectLivePagesResult {
  livePageByBaseID: Record<string, IPickPageItem>;
  /** Pulls every looked-up record again. Throws, so a caller's error toast can surface the failure. */
  handleRefetch: () => Promise<unknown>;
  isFetching: boolean;
}

/**
 * What a set of picked pages currently is in the admin — the title and artwork a rename left stale on
 * the variant, and the template each one renders. Each pick type lives behind its own query, so a
 * lookup has to fetch all of them and merge the results: asking the product API for a page or
 * collection id would only ever error. The homepage has no record and so never appears here.
 */
export const useVariantUrlRedirectLivePages = ({
  productBaseIDs,
  pageBaseIDs,
  collectionBaseIDs,
  articleBaseIDs,
  templateAssetKeys,
}: IUseVariantUrlRedirectLivePagesParams): IUseVariantUrlRedirectLivePagesResult => {
  const productVariables: PickPageProductsQueryVariables = {
    first: toFirstCount(productBaseIDs.length),
    query: toPlatformIdsQuery(productBaseIDs),
  };
  const pageVariables: PickPagePagesQueryVariables = {
    first: toFirstCount(pageBaseIDs.length),
    query: toPlatformIdsQuery(pageBaseIDs),
  };
  const collectionVariables: PickPageCollectionsQueryVariables = {
    first: toFirstCount(collectionBaseIDs.length),
    query: toPlatformIdsQuery(collectionBaseIDs),
  };
  const articleVariables: PickPageArticlesQueryVariables = {
    first: toFirstCount(articleBaseIDs.length),
    query: toPlatformIdsQuery(articleBaseIDs),
  };

  const {
    data: productData,
    refetch: refetchProducts,
    isFetching: isFetchingProducts,
  } = useQuery<PickPageProductsQueryResponse>(
    [PICK_PAGE_RESOURCE_QUERY_KEY.PRODUCT, productVariables],
    shopifyAdminFetcher<PickPageProductsQueryResponse, PickPageProductsQueryVariables>(
      PickPageProductsDocument,
      productVariables,
    ),
    {
      enabled: productBaseIDs.length > 0,
      keepPreviousData: true,
      refetchOnWindowFocus: true,
      staleTime: VARIANT_URL_LIVE_TITLE_STALE_TIME_MS,
      onError: handleLivePageQueryError,
    },
  );

  const {
    data: pageData,
    refetch: refetchPages,
    isFetching: isFetchingPages,
  } = useQuery<PickPagePagesQueryResponse>(
    [PICK_PAGE_RESOURCE_QUERY_KEY.PAGE, pageVariables],
    shopifyAdminFetcher<PickPagePagesQueryResponse, PickPagePagesQueryVariables>(
      PickPagePagesDocument,
      pageVariables,
    ),
    {
      enabled: pageBaseIDs.length > 0,
      keepPreviousData: true,
      refetchOnWindowFocus: true,
      staleTime: VARIANT_URL_LIVE_TITLE_STALE_TIME_MS,
      onError: handleLivePageQueryError,
    },
  );

  const {
    data: collectionData,
    refetch: refetchCollections,
    isFetching: isFetchingCollections,
  } = useQuery<PickPageCollectionsQueryResponse>(
    [PICK_PAGE_RESOURCE_QUERY_KEY.COLLECTION, collectionVariables],
    shopifyAdminFetcher<PickPageCollectionsQueryResponse, PickPageCollectionsQueryVariables>(
      PickPageCollectionsDocument,
      collectionVariables,
    ),
    {
      enabled: collectionBaseIDs.length > 0,
      keepPreviousData: true,
      refetchOnWindowFocus: true,
      staleTime: VARIANT_URL_LIVE_TITLE_STALE_TIME_MS,
      onError: handleLivePageQueryError,
    },
  );

  const {
    data: articleData,
    refetch: refetchArticles,
    isFetching: isFetchingArticles,
  } = useQuery<PickPageArticlesQueryResponse>(
    [PICK_PAGE_RESOURCE_QUERY_KEY.ARTICLE, articleVariables],
    shopifyAdminFetcher<PickPageArticlesQueryResponse, PickPageArticlesQueryVariables>(
      PickPageArticlesDocument,
      articleVariables,
    ),
    {
      enabled: articleBaseIDs.length > 0,
      keepPreviousData: true,
      refetchOnWindowFocus: true,
      staleTime: VARIANT_URL_LIVE_TITLE_STALE_TIME_MS,
      onError: handleLivePageQueryError,
    },
  );

  const livePageByBaseID = useMemo(() => {
    const map: Record<string, IPickPageItem> = {};

    // Keyed on the mapped row rather than the node: Shopify names a record with a GID, and only a
    // product's pick is stored that way — the mapper is what puts a baseID back in the shape the
    // caller looks one up by.
    const collect = (items: Array<IPickPageItem | undefined>) =>
      items.forEach((item) => {
        if (item?.baseID) map[item.baseID] = item;
      });

    collect(
      (productData?.products.edges ?? []).map((edge) =>
        edge.node ? toPickPageItemFromProduct({ node: edge.node, templateAssetKeys }) : undefined,
      ),
    );
    collect(
      (pageData?.pages.edges ?? []).map((edge) =>
        edge.node ? toPickPageItemFromPage({ node: edge.node, templateAssetKeys }) : undefined,
      ),
    );
    collect(
      (collectionData?.collections.edges ?? []).map((edge) =>
        edge.node
          ? toPickPageItemFromCollection({ node: edge.node, templateAssetKeys })
          : undefined,
      ),
    );
    collect(
      (articleData?.articles.edges ?? []).map((edge) =>
        edge.node ? toPickPageItemFromArticle({ node: edge.node, templateAssetKeys }) : undefined,
      ),
    );

    return map;
  }, [productData, pageData, collectionData, articleData, templateAssetKeys]);

  // The global staleTime keeps a record cached for days, so a caller's Refresh has to pull them
  // again. v4 `refetch()` resolves with an error result instead of rejecting, so each is opted into
  // throwing for the error toast at the call site.
  const handleRefetch = useCallback(
    () =>
      Promise.all([
        productBaseIDs.length ? refetchProducts({ throwOnError: true }) : undefined,
        pageBaseIDs.length ? refetchPages({ throwOnError: true }) : undefined,
        collectionBaseIDs.length ? refetchCollections({ throwOnError: true }) : undefined,
        articleBaseIDs.length ? refetchArticles({ throwOnError: true }) : undefined,
      ]),
    [
      productBaseIDs.length,
      pageBaseIDs.length,
      collectionBaseIDs.length,
      articleBaseIDs.length,
      refetchProducts,
      refetchPages,
      refetchCollections,
      refetchArticles,
    ],
  );

  return {
    livePageByBaseID,
    handleRefetch,
    isFetching:
      isFetchingProducts || isFetchingPages || isFetchingCollections || isFetchingArticles,
  };
};
